import { css } from 'styled-components';
import type { CSSProp, ThemeProps } from 'styled-components';
import type { AllColorType, ThemeType } from './types';
import { COLOR_PICKER_COLORS } from '../util/vars';
import { darken, lighten, readableColor, transparentize } from 'polished';

export const DEFAULT_TRANSITION_FUNC = 'cubic-bezier(0.45,0.03,0.55,0.95)';
export const SLOW_TRANSITION_DURATION = '150ms';
export const DEFAULT_TRANSITION_DURATION = '450ms';
export const MEDIUM_TRANSITION_DURATION = '850ms';
export const LONG_TRANSITION_DURATION = '1250ms';

const POLISHED_VALUES: {
  default: number,
  high: {
    default: number,
    colors: {
      [key in AllColorType]?: number;
    }
  },
  light: {
    default: number,
    colors: {
      [key in AllColorType]?: number;
    }
  },
  mostLight: {
    default: number,
    colors: {
      [key in AllColorType]?: number;
    }
  }
} = {
  default: 0.8,
  high: {
    default: 0.9,
    colors: {
      primary: 0.97,
    },
  },
  light: {
    default: 0.3,
    colors: {
      primary: 0.6,
    },
  },
  mostLight: {
    default: 0.4,
    colors: {
      negative: 0.36,
      primary: 0.7,
    },
  },
};

export const CHECKBOX_SIZE = 15;

/**
 * Transforms animation duration from string to integer.
 * @param duration Animation duration as {x}ms.
 */
export const getAnimationDurationAsInt = (duration = DEFAULT_TRANSITION_DURATION): number => {
  return parseInt(duration.replace('ms', ''), 10);
};

/**
 * Returns an object with the transformed colors used in components.
 * @param props Theme, $color props.
 */
export const getPolishedColors = (
  props: ThemeProps<ThemeType> & { $color: AllColorType },
  opacity: 'solid' | 'transparent' = 'transparent',
): {
  main: {
    color: string;
    readable: string;
  };
  most_light: {
    color: string;
    readable: string;
  };
  light: {
    color: string;
    readable: string;
  };
  most_dark: {
    color: string;
    readable: string;
  };
  dark: {
    color: string;
    readable: string;
  };
} => {
  const opacityIsSolid = opacity === 'solid';
  const main = props.theme.colors[props.$color];
  const mostLight = opacityIsSolid ? lighten(POLISHED_VALUES.mostLight.colors[props.$color] ?? POLISHED_VALUES.mostLight.default, main) : transparentize(POLISHED_VALUES.high.colors[props.$color] ?? POLISHED_VALUES.high.default, main);
  const light = opacityIsSolid ? lighten(POLISHED_VALUES.light.colors[props.$color] ?? POLISHED_VALUES.light.default, main) : transparentize(POLISHED_VALUES.default, main);
  const mostDark = darken(POLISHED_VALUES.high.default, main);
  const dark = darken(0.2, main);

  return {
    main: {
      color: main,
      readable: readableColor(main),
    },
    most_light: {
      color: mostLight,
      readable: readableColor(opacityIsSolid ? mostLight : lighten(POLISHED_VALUES.high.default, main)),
    },
    light: {
      color: light,
      readable: readableColor(opacityIsSolid ? light : lighten(POLISHED_VALUES.default, main)),
    },
    most_dark: {
      color: mostDark,
      readable: readableColor(mostDark),
    },
    dark: {
      color: dark,
      readable: readableColor(dark),
    },
  };
};

/**
 * Returns focus styles.
 * @param color
 * @param isInset
 */
export const getFocusStyle = (color: AllColorType | string, isInset = false): CSSProp => {
  return css`
    outline: none;
    box-shadow: ${isInset && 'inset'} 0 0 0 0.25rem ${transparentize(POLISHED_VALUES.default, color)};
  `;
};

/**
 * Returns integer of value in pixels.
 * @param pixels
 */
export const getPixelInteger = (pixels: string): number => {
  return parseInt(pixels.replace('px', ''));
};

export const getDefaultTagColor = (id: number): number => {
  const total_colors = Object.keys(COLOR_PICKER_COLORS).length;
  const colorIndex = id % total_colors;
  return colorIndex;
};
